<template>
  <div>
    <label
      v-if="label"
      :for="name"
      class="text-background-fg/70 mb-1 block text-xs font-medium"
    >
      {{ label }}
    </label>
    <div class="relative mt-1 rounded-md shadow-sm">
      <input
        :type="type_"
        :name="name"
        :class="[
          'border-1 border-background-fg/8 block w-full rounded-md pr-10 text-sm font-semibold focus:outline-none sm:text-sm',
          hasError
            ? 'border-error text-error placeholder-error focus:border-error focus:ring-error'
            : 'focus:ring-primary focus:border-primary',
        ]"
        :placeholder="placeholder"
        :value="inputValue"
        :aria-invalid="hasError"
        enterkeyhint="done"
        :disabled="disabled"
        v-bind="extraInputAttributes"
        :autocomplete="
          type === 'pin' ? 'off' : autocomplete
        "
        @change="handleChange"
        @blur="handleBlur"
      />
      <div
        class="absolute inset-y-0 right-0 flex items-center pr-3"
      >
        <button
          type="button"
          class="flex items-center"
          tabindex="-1"
          @click="toggleVisible"
        >
          <template v-if="isVisible">
            <IconHeroiconsOutlineEye />
          </template>
          <template v-else>
            <IconHeroiconsOutlineEyeOff />
          </template>
        </button>
      </div>
    </div>
    <template v-if="hasError">
      <p class="text-error mt-1 text-xs font-medium">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
import type { InputHTMLAttributes } from "vue"

const props = withDefaults(
  defineProps<{
    modelValue?: string
    name: string
    label: string
    placeholder?: string
    disabled?: boolean
    type?: "password" | "pin"
    autocomplete?: string
  }>(),
  {
    modelValue: "",
    type: "password",
    placeholder: "",
    disabled: false,
    autocomplete: "off",
  }
)

const name = toRef(props, "name")
type InputType = "password" | "text"

const type_ = computed<InputType>(() =>
  isVisible.value ? "text" : "password"
)
const isVisible = ref(false)
const toggleVisible = () => {
  isVisible.value = !isVisible.value
}
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField(name, undefined, {
  initialValue: props.modelValue,
})

const hasError = computed(() => !!errorMessage.value)

const extraInputAttributes = computed<InputHTMLAttributes>(
  () =>
    props.type === "pin"
      ? {
          pattern: "[0-9]*",
          inputmode: "numeric",
          foo: "1",
        }
      : {}
)
</script>
